import { Box, CircularProgress, Container, Typography } from '@mui/material'
import { atom, useAtom, useAtomValue, useSetAtom } from 'jotai'
import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import { useSearchParams } from 'react-router'

import ChatLayout from '../components/chats/ChatLayout'
import { selectedChatRoomIdAtom } from '../contexts/AtomUtil'
import { fullScreenAtom } from '../contexts/AtomUtil'
import { useUsers } from '../contexts/UserContext'
import { authDataAtom } from '../domains/auth/AtomAuth'
import { isAuthenticatedAtom } from '../domains/auth/AuthSelectors'
import {
  chatErrorAtom,
  chatLoadingAtom,
  connectionStateAtom,
  chatRoomsAtom,
  messagesAtomFamily,
  unreadCountsAtomFamily,
  userMapAtom,
  loadedHistoryRoomsAtom,
  lastSyncTimestampsAtom,
} from '../domains/chat/AtomChats'
import { selectedMessagesAtom } from '../domains/chat/AtomChatsSelectors'
import { receiveMessageCommandAtom } from '../domains/chat/ChatCommand'
import { useChatResync } from '../domains/chat/useChatResync'
import { currentUserAtom } from '../domains/user/AtomUsers'
import ChatService from '../services/ChatService'
import { transformServerMessages, transformServerRooms } from '../utils/chatUtils'

/**
 * ChatPage - Main chat page showing ChatLayout with room list and conversation
 * Displays all chat rooms and allows user to select and chat with instructors
 */
export default function ChatPage() {
  const [searchParams, setSearchParams] = useSearchParams()

  const currentUser = useAtomValue(currentUserAtom)
  const authData = useAtomValue(authDataAtom)
  const isAuthenticated = useAtomValue(isAuthenticatedAtom)
  const { fetchUsers } = useUsers()

  // Atom state management
  const [chatRooms, setChatRooms] = useAtom(chatRoomsAtom)

  const [userMap, setUserMap] = useAtom(userMapAtom)
  const [loading, setLoading] = useAtom(chatLoadingAtom)
  const [error, setError] = useAtom(chatErrorAtom)
  const [connectionState, setConnectionState] = useAtom(connectionStateAtom)
  const [loadedHistoryRooms, setLoadedHistoryRooms] = useAtom(loadedHistoryRoomsAtom)
  const [lastSyncTimestamps, setLastSyncTimestamps] = useAtom(lastSyncTimestampsAtom)

  const chatRoomId = useAtomValue(selectedChatRoomIdAtom)
  const fullScreen = useAtomValue(fullScreenAtom)

  // Get room messages atom for current room
  const roomMessagesAtom = useMemo(() => (chatRoomId ? messagesAtomFamily(chatRoomId) : null), [chatRoomId])
  // Create a fallback atom that returns empty array
  const fallbackAtom = useMemo(() => atom([]), [])
  const [roomMessages, setRoomMessages] = useAtom(roomMessagesAtom || fallbackAtom)

  // Unread counts atom for current user
  const [, setUnreadCounts] = useAtom(unreadCountsAtomFamily(currentUser?.resourceId || ''))

  // Command atom for receiving messages
  const receiveMessageCommand = useSetAtom(receiveMessageCommandAtom)

  // Auto-resync on WebSocket reconnect and get quickResync function
  const quickResync = useChatResync()

  // Refs for stable references
  const chatServiceRef = useRef(ChatService)

  const updateUrl = useCallback(
    (roomId) => {
      const newSearchParams = new URLSearchParams(searchParams)
      if (roomId) {
        newSearchParams.set('roomId', roomId)
      } else {
        newSearchParams.delete('roomId')
      }
      setSearchParams(newSearchParams, { replace: false })
    },
    [searchParams, setSearchParams],
  )

  // Load all rooms on mount or when user logs in
  // This ensures that when app starts and user is already logged in, we query for group chat info
  useEffect(() => {
    const loadRooms = async () => {
      if (!isAuthenticated) {
        setLoading(false)
        return
      }

      try {
        setLoading(true)
        setError(null)

        // Get all rooms from server
        const serverRooms = await chatServiceRef.current.getRooms()

        // Build user map from all rooms
        const userIds = new Set()
        serverRooms.forEach((room) => {
          room.userId?.forEach((id) => userIds.add(id))
          if (room.lastMessage?.senderId) {
            userIds.add(room.lastMessage.senderId)
          }
        })

        // Fetch user information for all unique user IDs
        const userIdsArray = Array.from(userIds)
        if (userIdsArray.length > 0) {
          const userInfos = await fetchUsers({ userIds: userIdsArray })
          const newUserMap = {}
          userInfos.forEach((userInfo) => {
            newUserMap[userInfo.resourceId] = {
              id: userInfo.resourceId,
              name: `${userInfo.firstName} ${userInfo.lastName}`,
              resourceId: userInfo.resourceId,
            }
          })
          setUserMap(newUserMap)

          // Transform rooms to conversations format
          // Server now provides unreadCount and lastReadTimestamp
          const transformedRooms = transformServerRooms(serverRooms, currentUser.resourceId, newUserMap)

          // Update unreadCounts atom with server data
          const newUnreadCounts = {}
          transformedRooms.forEach((room) => {
            if (room.unreadCount > 0) {
              newUnreadCounts[room.id] = room.unreadCount
            }
          })
          setUnreadCounts(newUnreadCounts)

          setChatRooms(transformedRooms)
        } else {
          setChatRooms([])
        }

        setLoading(false)
      } catch (error) {
        console.error('Failed to load rooms:', error)
        setError(error.message || 'Failed to load chat rooms')
        setLoading(false)
      }
    }

    loadRooms()
  }, [isAuthenticated]) // eslint-disable-line react-hooks/exhaustive-deps

  // Connect WebSocket once on mount
  useEffect(() => {
    const connectWebSocket = async () => {
      if (!isAuthenticated) {
        return
      }

      try {
        await chatServiceRef.current.connect(authData.token)
      } catch (error) {
        console.error('Failed to connect WebSocket:', error)
      }
    }

    connectWebSocket()
  }, [isAuthenticated, authData?.token])

  // When the app returns to foreground, verify connection health, reconnect if needed, and trigger quick resync
  useEffect(() => {
    if (!isAuthenticated) return

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        chatServiceRef.current.ensureActiveConnection?.()
        // Trigger quick resync when app returns from background (especially on iPhone)
        // Use a small delay to ensure WebSocket connection is ready
        setTimeout(() => {
          quickResync()
        }, 500)
      }
    }

    const handleWindowFocus = () => {
      chatServiceRef.current.ensureActiveConnection?.()
      // Trigger quick resync when window gains focus
      setTimeout(() => {
        quickResync()
      }, 500)
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('focus', handleWindowFocus)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('focus', handleWindowFocus)
    }
  }, [isAuthenticated, quickResync])

  // Handle room selection: load messages and mark as read
  useEffect(() => {
    const loadRoomMessages = async () => {
      if (!chatRoomId || !isAuthenticated) {
        return
      }

      // Mark messages as read for this room in both conversations and unreadCounts atom
      setChatRooms((prev) =>
        prev.map((conv) =>
          conv.id === chatRoomId
            ? {
                ...conv,
                unreadCount: 0,
              }
            : conv,
        ),
      )

      // Update unreadCounts atom (remove this room's unread count)
      setUnreadCounts((prev) => {
        const newCounts = { ...prev }
        delete newCounts[chatRoomId]
        return newCounts
      })

      // If history messages already loaded, mark as read and return
      // Don't check message length because WebSocket messages might exist but history not loaded
      if (loadedHistoryRooms.has(chatRoomId)) {
        if (roomMessages && roomMessages.length > 0) {
          const lastMessage = roomMessages[roomMessages.length - 1]
          if (lastMessage) {
            try {
              await chatServiceRef.current.markAsRead(chatRoomId, lastMessage.id, lastMessage.timestamp)
            } catch (markReadError) {
              console.error('Failed to mark room as read:', markReadError)
            }
          }
        }
        return
      }

      try {
        // Load historical messages
        // Use lastSyncTimestamp if available to only fetch new messages
        const since = lastSyncTimestamps[chatRoomId] || null
        const serverMessages = await chatServiceRef.current.getMessages(chatRoomId, 50, since)

        // Build user map for messages if needed
        const userIds = new Set()
        serverMessages.forEach((msg) => {
          if (msg.senderId) userIds.add(msg.senderId)
        })

        // Fetch missing user information
        const missingUserIds = Array.from(userIds).filter((id) => !userMap[id])
        console.log('missingUserIds =>', missingUserIds)
        let updatedUserMap = userMap
        if (missingUserIds.length > 0) {
          const userInfos = await fetchUsers({ userIds: missingUserIds })
          updatedUserMap = { ...userMap }
          userInfos.forEach((userInfo) => {
            updatedUserMap[userInfo.resourceId] = {
              id: userInfo.resourceId,
              name: `${userInfo.firstName} ${userInfo.lastName}`,
              resourceId: userInfo.resourceId,
            }
          })
          setUserMap(updatedUserMap)
        }

        // Transform messages using updated user map
        const transformedMessages = transformServerMessages(serverMessages, currentUser.resourceId, updatedUserMap)

        // Merge with existing messages (from WebSocket) and sort by timestamp
        // Use current roomMessages from atom
        const existingMessages = roomMessages || []

        // Create a map to deduplicate messages by id
        const messageMap = new Map()

        // Add existing messages first
        existingMessages.forEach((msg) => {
          messageMap.set(msg.id, msg)
        })

        // Add new history messages (will overwrite duplicates if any)
        transformedMessages.forEach((msg) => {
          messageMap.set(msg.id, msg)
        })

        // Convert back to array and sort by timestamp
        const mergedMessages = Array.from(messageMap.values()).sort((a, b) => a.timestamp - b.timestamp)

        // Update state with merged messages
        setRoomMessages(mergedMessages)

        // Update last sync timestamp (use the newest message timestamp)
        if (mergedMessages.length > 0) {
          const newestTimestamp = Math.max(...mergedMessages.map((msg) => msg.timestamp))
          setLastSyncTimestamps((prev) => ({
            ...prev,
            [chatRoomId]: newestTimestamp,
          }))
        }

        // Mark this room's history as loaded
        setLoadedHistoryRooms((prev) => {
          const newSet = new Set(prev)
          newSet.add(chatRoomId)
          return newSet
        })

        // Mark room as read on server if there are messages
        if (mergedMessages.length > 0) {
          const lastMessage = mergedMessages[mergedMessages.length - 1]
          try {
            await chatServiceRef.current.markAsRead(chatRoomId, lastMessage.id, lastMessage.timestamp)
          } catch (markReadError) {
            console.error('Failed to mark room as read:', markReadError)
            // Don't throw - this is not critical
          }
        }
      } catch (error) {
        console.error('Failed to load room messages:', error)
        setError(error.message || 'Failed to load messages')
      }
    }

    loadRoomMessages()
  }, [chatRoomId, roomMessages, setRoomMessages]) // eslint-disable-line react-hooks/exhaustive-deps

  // Set up WebSocket message handler
  useEffect(() => {
    const handleMessage = (serverMessage) => {
      // Use command atom to handle the message
      receiveMessageCommand(serverMessage)
    }

    chatServiceRef.current.onMessage(handleMessage)
    // Intentionally minimal dependencies - command atom is stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [receiveMessageCommand])

  // Set up connection state handler
  useEffect(() => {
    const handleConnectionState = (state) => {
      setConnectionState(state)
    }

    chatServiceRef.current.onConnectionState(handleConnectionState)
  }, [setConnectionState])

  // Handle room selection
  const handleSelectRoom = useCallback(
    (roomId) => {
      updateUrl(roomId)
    },
    [updateUrl],
  )

  // Handle sending messages
  const handleSendMessage = useCallback(
    (content) => {
      if (!content.trim() || !chatRoomId) return

      try {
        chatServiceRef.current.sendMessage(chatRoomId, content.trim())
      } catch (error) {
        console.error('Failed to send message:', error)
        setError(error.message || 'Failed to send message')
      }
    },
    [chatRoomId, setError],
  )

  // Note: WebSocket connection is maintained across navigation
  // and only disconnected on logout (handled in AuthContext)

  // Memoized derived values to prevent unnecessary recalculations
  const selectedConversation = useMemo(() => {
    return chatRooms.find((conv) => conv.id === chatRoomId)
  }, [chatRooms, chatRoomId])

  // Use selector for selected messages
  const selectedMessages = useAtomValue(selectedMessagesAtom)

  // Prepare chat header props (memoized to prevent ChatLayout re-renders)
  const chatHeaderProps = useMemo(
    () => ({
      title: selectedConversation?.title || 'Chat',
      subtitle: connectionState === 'connected' ? 'Connected' : connectionState === 'connecting' ? 'Connecting...' : connectionState === 'disconnected' ? 'Disconnected' : 'Error',
    }),
    [selectedConversation?.title, connectionState],
  )

  // Loading state
  if (loading && chatRooms.length === 0) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, height: 'calc(100vh - 120px)' }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress />
        </Box>
      </Container>
    )
  }

  // Error state
  if (error && chatRooms.length === 0) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, height: 'calc(100vh - 120px)' }}>
        <Box sx={{ py: 4 }}>
          <Typography color="error" align="center">
            {error}
          </Typography>
        </Box>
      </Container>
    )
  }

  // Main chat layout (similar to DevChat.js)
  return (
    <Container
      maxWidth="lg"
      sx={{
        ...(fullScreen
          ? {
              height: '100%',
              overflow: 'hidden',
              p: 0,
            }
          : {
              pt: { xs: 0, sm: 3 },
              pb: { xs: 0, sm: 0, md: 3 },
              px: { xs: 0, sm: 3 },
              height: {
                xs: 'calc(100vh - 145px)',
                sm: 'calc(100vh - 145px)',
                md: 'calc(100vh - 144px)',
              },
              overflow: 'hidden',
            }),
      }}
    >
      <ChatLayout
        conversations={chatRooms}
        selectedConversationId={chatRoomId}
        onSelectConversation={handleSelectRoom}
        messages={selectedMessages}
        onSendMessage={handleSendMessage}
        currentUserId={currentUser?.resourceId}
        chatHeaderProps={chatHeaderProps}
        connectionState={connectionState}
        fullScreen={fullScreen}
        sx={{ height: '100%', overflow: 'hidden' }}
      />
    </Container>
  )
}
